import { LitElement, css, html } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { when } from 'lit/directives/when.js';
import { repeat } from 'lit/directives/repeat.js';
import { consume } from '@lit/context';
import { ContextChangeController } from '../ns-services/context-change.js';
import { nsDirectoryServiceContext, nsDirectoryStateContext, nsLoaderContext } from '../ns-services/contexts.js';
import type { NSLoader } from '../ns-services/ns-loader-service.js';
import { runBehindShutter } from '../helpers/reload-behind-shutter.js';

import { ButtressDbService, buttressDbServiceContext } from '@buttress/crag';

import { nsSharedStyles, nsLayoutStyles } from '../ns-styles/index.js';

import { NSDirectoryService } from '../ns-services/ns-directory-service.js';

import Person from '../models/person.js';
import Company from '../models/company.js';
import { personInitials } from '../helpers/person.js';

import './ns-verification-badge.js';
import { personVerificationState } from '../helpers/person-verification.js';

@customElement('ns-company-switcher')
export class NSCompanySwitcher extends LitElement {
  @consume({ context: buttressDbServiceContext })
  private __db?: ButtressDbService;

  @consume({ context: nsLoaderContext })
  private __loader?: NSLoader;

  @consume({ context: nsDirectoryServiceContext })
  private __dir?: NSDirectoryService;

  private __profileChanges = new ContextChangeController(this, nsDirectoryStateContext, async () => {
    await this.__profileChange();
  });

  private __initials: string = '';

  private __person?: Person;

  private __documentClickListener = (ev: MouseEvent) => this.__handleDocumentClick(ev);

  // The directory refetches the person on any people change (a claim verified, a name edited) and says so: the badge, name,
  // role and initials follow it live
  private readonly __onMeUpdated = () => {
    this.__person = this.__dir?.me;
    this.__initials = this.__person ? personInitials(this.__person) : '';
    this.requestUpdate();
  };

  @state()
  private __selectedCompany?: Company;

  @state()
  private __myCompanies: {
    value: string;
    label: string;
    role: string | null;
    logo?: string;
    brandColor?: string;
  }[] = [];

  @state()
  private __companyMenuOpen: boolean = false;

  @state()
  private __ripples: { id: number; x: number; y: number; size: number }[] = [];

  private __rippleId: number = 0;

  @state()
  private __profileLoaded: boolean = false;

  @state()
  private __avatarFailed: boolean = false;

  static styles = [
    nsSharedStyles,
    nsLayoutStyles,
    css`
      :host {
        display: block;
        position: relative;
      }

      /* Figma's company changer: a 50px teal pill */
      .profile-info {
        position: relative;
        z-index: 901;
        overflow: hidden;

        box-sizing: border-box;
        height: 100%;
        min-height: 50px;
        max-width: 380px;

        gap: var(--wa-space-xs);
        padding: var(--wa-space-2xs) var(--wa-space-s) var(--wa-space-2xs) var(--wa-space-2xs);
        border-radius: var(--wa-border-radius-pill);
        background: var(--ns-theme-teal);

        border: none;
        outline: none;
        font: inherit;
        color: var(--ns-color-inverse-text);
        text-align: left;
      }

      .profile-info:focus-visible {
        outline: 2px solid var(--wa-color-focus);
        outline-offset: 2px;
      }

      .profile-info .details {
        min-width: 0;
      }

      .profile-info .username {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;

        font-size: var(--wa-font-size-l);
        font-weight: 500;
        line-height: 1.1;
      }

      .profile-info .role {
        display: flex;
        align-items: center;
        gap: var(--wa-space-3xs);
        white-space: nowrap;

        font-size: var(--wa-font-size-3xs);
        font-weight: 300;
      }

      .profile-info .role span {
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;

        padding: 0 var(--wa-space-2xs);
        border-radius: var(--wa-border-radius-pill);
        background: var(--wa-color-success-fill-loud);
        color: var(--wa-color-success-on-loud);

        font-weight: 600;
      }

      .avatar {
        position: relative;
        flex: none;
      }

      .avatar ns-verification-badge {
        position: absolute;
        right: -2px;
        bottom: -2px;

        --icon-width: 14px;
        --icon-height: 14px;
      }

      .company-logo {
        flex: none;

        width: 56px;
        height: 32px;

        background-size: contain;
        background-repeat: no-repeat;
        background-position: center;
      }

      .profile {
        box-sizing: border-box;
        width: 42px;
        height: 42px;
        vertical-align: top;

        border-radius: var(--wa-border-radius-circle);
        object-fit: cover;
      }

      .initials {
        color: var(--wa-color-brand-on-loud);
        background-color: var(--wa-color-brand-fill-loud);

        font-weight: 700;
        font-size: var(--wa-font-size-m);
      }

      .ripple {
        position: absolute;
        border-radius: 100%;

        background: color-mix(in srgb, var(--ns-theme-white) 35%, transparent);
        pointer-events: none;

        transform: scale(0);
        opacity: 1;

        animation: ns-company-switcher-ripple 500ms ease-out forwards;
      }

      @keyframes ns-company-switcher-ripple {
        to {
          transform: scale(1);
          opacity: 0;
        }
      }

      /* Figma's 20px chevron disc; the chevron is Type rather than Figma's white so it reads on the grey */
      .chevron-badge {
        flex: none;
        display: inline-flex;
        align-items: center;
        justify-content: center;

        width: 20px;
        height: 20px;

        border-radius: var(--wa-border-radius-circle);
        background: var(--ns-theme-grey-mid);
        color: var(--wa-color-text-normal);
      }

      .chevron-badge svg {
        width: 10px;
        height: 6px;
        transition: transform 0.2s;
      }

      .chevron-badge.open svg {
        transform: rotate(180deg);
      }

      .company-menu {
        position: absolute;
        top: 100%;
        right: 0;
        width: 100%;
        margin-top: -30px;

        max-height: 280px;
        overflow-y: auto;
        box-sizing: border-box;

        display: flex;
        flex-direction: column;
        gap: var(--wa-space-2xs);

        background: var(--ns-theme-grey-mid);
        border-radius: 0 0 var(--wa-border-radius-l) var(--wa-border-radius-l);
        box-shadow: 0px 4px 8px 0px color-mix(in srgb, var(--ns-theme-black) 20%, transparent);

        padding: var(--wa-space-2xl) var(--wa-space-2xs) var(--wa-space-2xs);
        z-index: 900;
      }

      .company-menu-item {
        display: flex;
        align-items: stretch;

        width: 100%;
        box-sizing: border-box;
        text-align: left;

        border: none;
        background: var(--ns-theme-container);
        color: var(--ns-theme-grey-dark);
        font: inherit;
        font-size: var(--wa-font-size-xs);

        padding: 0;
        border-radius: var(--wa-border-radius-pill);
        overflow: hidden;
      }

      .company-menu-item:hover .company-menu-text {
        background: var(--ns-theme-container);
      }

      .company-menu-logo {
        flex: none;
        align-self: center;

        width: 24px;
        height: 24px;
        margin-left: var(--wa-space-2xs);
        border-radius: var(--wa-border-radius-circle);

        background-color: var(--ns-theme-type);
        background-size: 18px 18px;
        background-repeat: no-repeat;
        background-position: center;
      }

      .company-menu-text {
        flex: 1;

        padding: var(--wa-space-2xs) var(--wa-space-xs);
      }

      .company-menu-text strong {
        font-weight: 700;
      }
    `,
  ];

  async connectedCallback() {
    super.connectedCallback();
    await this.updateComplete;

    if (!this.__db) throw new Error('Unable to find db service');

    if (!this.__dir) throw new Error('Directory service not found');
    await this.__dir.initialised;

    await this.__profileChange();
    this.__dir.addEventListener('me-updated', this.__onMeUpdated);
    window.addEventListener('click', this.__documentClickListener, {
      capture: true,
    });
  }

  disconnectedCallback() {
    this.__dir?.removeEventListener('me-updated', this.__onMeUpdated);
    window.removeEventListener('click', this.__documentClickListener);
    super.disconnectedCallback();
  }

  private async __profileChange() {
    if (!this.__dir) throw new Error('Can not find directory service');

    this.__person = this.__dir.me;
    this.__selectedCompany = this.__dir.company;
    this.__initials = this.__dir?.me ? personInitials(this.__dir.me) : '';
    this.__profileLoaded = true;

    const roleByCompanyId = new Map(this.__dir.people.map((p) => [p.companyId, p.jobTitle]));
    const companyIds = [...roleByCompanyId.keys()];

    const query = await this.__db?.query(
      'company',
      {
        id: {
          $in: companyIds,
        },
      },
      {
        limit: 0,
      },
    );
    if (!query) throw new Error('Unable to query company');
    const companies = query.results as unknown as Company[];
    this.__myCompanies = companies.map((c: Company) => {
      return {
        value: c.id,
        label: c.name,
        role: roleByCompanyId.get(c.id) ?? null,
        logo: c.assets?.logoImageId || c.profileInfo?.logoUrl,
        brandColor: c.profileInfo?.brandColor,
      };
    });
  }

  private __toggleCompanyMenu(ev: MouseEvent) {
    this.__spawnRipple(ev);
    this.__companyMenuOpen = !this.__companyMenuOpen;
  }

  private __spawnRipple(ev: MouseEvent) {
    const button = ev.currentTarget as HTMLElement;
    const rect = button.getBoundingClientRect();
    const size = Math.max(rect.width, rect.height) * 2;

    this.__ripples = [
      ...this.__ripples,
      {
        id: this.__rippleId++,
        x: ev.clientX - rect.left - size / 2,
        y: ev.clientY - rect.top - size / 2,
        size,
      },
    ];
  }

  private __removeRipple(id: number) {
    this.__ripples = this.__ripples.filter((ripple) => ripple.id !== id);
  }

  private __closeCompanyMenu() {
    this.__companyMenuOpen = false;
  }

  private __handleDocumentClick(ev: MouseEvent) {
    if (!this.__companyMenuOpen) return;

    // A `window`-level listener sees `ev.target` retargeted to the outermost shadow host
    // crossed, so containment checks must use the real, un-retargeted composed path instead.
    const path = ev.composedPath();
    if (path.includes(this)) return;

    this.__closeCompanyMenu();
  }

  private async __selectCompany(companyId: string) {
    this.__closeCompanyMenu();
    if (companyId === this.__selectedCompany?.id) return;

    // The shutter closes first; the switch happens behind it, then the app reloads, since crag's store still holds the
    // previous company's entities, some under another policy's projection
    await runBehindShutter(this.__loader, 'Switching company…', async () => {
      await this.__dir?.selectCompanyProfile(companyId);
    });
  }

  private __companyLogoStyle(company: { logo?: string; brandColor?: string }): string {
    const styles: string[] = [];
    if (company.brandColor) styles.push(`background-color: ${company.brandColor}`);
    if (company.logo) styles.push(`background-image: url(${company.logo})`);
    return styles.join('; ');
  }

  private __verificationState() {
    return personVerificationState(this.__person);
  }

  private __selectedCompanyLogo() {
    return this.__myCompanies.find((c) => c.value === this.__selectedCompany?.id)?.logo;
  }

  private __profileInfoStyle(): string {
    const brandColor = this.__selectedCompany?.profileInfo?.brandColor;
    return brandColor ? `background: ${brandColor}` : '';
  }

  protected override render() {
    return html`
      <button
        type="button"
        class="profile-info clickable layout horizontal center"
        style="${this.__profileInfoStyle()}"
        aria-label="Switch company"
        aria-haspopup="true"
        aria-expanded="${this.__companyMenuOpen}"
        @click="${this.__toggleCompanyMenu}"
      >
        <div class="avatar" ?hidden="${!this.__profileLoaded}">
          ${this.__person?.avatar && !this.__avatarFailed
            ? html`
                <img class="profile" src=${this.__person.avatar} alt="" @error=${() => (this.__avatarFailed = true)} />
              `
            : html`
                <div class="layout vertical center-center profile initials">${this.__initials}</div>
              `}
          <ns-verification-badge state=${this.__verificationState()}></ns-verification-badge>
        </div>
        <div class="layout vertical details">
          <div class="username">${this.__person?.forename} ${this.__person?.surname}</div>
          <div class="role">
            ${this.__person?.jobTitle ? this.__person?.jobTitle : 'Unknown role'}
            ${this.__selectedCompany
              ? html`
                  at
                  <span title=${this.__selectedCompany.name}>${this.__selectedCompany.name}</span>
                `
              : ''}
          </div>
        </div>
        <div class="flex"></div>
        ${when(
          this.__selectedCompanyLogo(),
          (logo) => html`
            <span class="company-logo" style="background-image: url(${logo})"></span>
          `,
        )}
        ${when(
          this.__myCompanies.length > 0,
          () => html`
            <span class="chevron-badge ${this.__companyMenuOpen ? 'open' : ''}">
              <svg viewBox="0 0 10.7 6.1" aria-hidden="true" focusable="false">
                <path d="M0.35 0.35 5.35 5.35 10.35 0.35" fill="none" stroke="currentColor" />
              </svg>
            </span>
          `,
        )}
        ${repeat(
          this.__ripples,
          (ripple) => ripple.id,
          (ripple) => html`
            <span
              class="ripple"
              style="width: ${ripple.size}px; height: ${ripple.size}px; left: ${ripple.x}px; top: ${ripple.y}px;"
              @animationend="${() => this.__removeRipple(ripple.id)}"
            ></span>
          `,
        )}
      </button>
      ${when(
        this.__companyMenuOpen && this.__myCompanies.length > 0,
        () => html`
          <div class="company-menu">
            ${repeat(
              this.__myCompanies,
              (c) => c.value,
              (c) => html`
                <button type="button" class="company-menu-item" @click="${() => this.__selectCompany(c.value)}">
                  <span class="company-menu-logo" style="${this.__companyLogoStyle(c)}"></span>
                  <span class="company-menu-text">
                    ${c.role
                      ? html`
                          ${c.role} at
                        `
                      : ''}
                    <strong>${c.label}</strong>
                  </span>
                </button>
              `,
            )}
          </div>
        `,
      )}
    `;
  }
}
